/**
 * Azure Application Insights Telemetry Service
 *
 * Initializes and exposes the Application Insights SDK for the Manager UX.
 * Connection string is loaded from the per-environment config so each environment
 * (dev, UAT, prod) reports to its own Application Insights resource.
 *
 * Usage:
 *   import { appInsights, reactPlugin } from '../telemetry/appInsights';
 *   appInsights.trackEvent({ name: 'ButtonClicked' });
 */
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { appConfig } from '../config';

const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();

const connectionString = appConfig.applicationInsightsConnectionString;

const appInsights = new ApplicationInsights({
  config: {
    connectionString: connectionString || undefined,
    enableAutoRouteTracking: true, // Track SPA page views automatically
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
    // Disable telemetry when no connection string is configured (local / test)
    disableTelemetry: !connectionString,
    /* ---- Sampling & buffering ---- */
    maxBatchInterval: 5000, // Flush every 5 s
    disableFetchTracking: false, // Track fetch/XHR calls
    /* ---- CORS correlation ----
     * Disabled to prevent App Insights from adding extra headers (Request-Id,
     * traceparent, Request-Context) to cross-origin API calls, which would
     * trigger CORS preflight failures if the API server doesn't whitelist them.
     * Enable these once the API's CORS policy allows App Insights headers.
     */
    enableCorsCorrelation: false,
    enableRequestHeaderTracking: false,
    enableResponseHeaderTracking: false,
  },
});

// Only load the SDK when we actually have a connection string
if (connectionString) {
  appInsights.loadAppInsights();

  /**
   * Telemetry initializer — automatically enriches every telemetry item
   * with the authenticated user's email.
   * Reads from the MSAL account stored in sessionStorage so it works
   * even before React components mount.
   */
  appInsights.addTelemetryInitializer((envelope) => {
    const props = envelope.data = envelope.data || {};
    props['appName'] = 'Manager-UX';

    try {
      // Try to find the active MSAL account in session/local storage
      // MSAL stores accounts with keys like "msal.<clientId>.<homeAccountId>"
      const storageKeys = [
        ...Object.keys(sessionStorage),
        ...Object.keys(localStorage),
      ];

      for (const key of storageKeys) {
        if (!key.includes('.idtoken')) continue;
        try {
          const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
          if (!raw) continue;
          const parsed = JSON.parse(raw);
          const claims = parsed?.secret
            ? JSON.parse(atob(parsed.secret.split('.')[1]))
            : null;
          if (claims) {
            props['userEmail'] = claims.email || claims.preferred_username || claims.upn || '';
            props['idp'] = claims.idp || claims.iss || '';
            break;
          }
        } catch {
          // Skip malformed entries
        }
      }
    } catch {
      // Never let telemetry initializer break the app
    }
  });

  appInsights.trackPageView(); // Record the initial page view
}

export { appInsights, reactPlugin, browserHistory };
